/**
 * markdown.ts — Utilitários para parsing de Markdown com suporte a wikilinks Obsidian
 *
 * Wikilinks suportados:
 *   [[Nota]] → link para a nota
 *   [[Nota|Alias]] → link com texto customizado
 *   [[Nota#Seção]] → link para seção específica
 */

/**
 * Converte wikilinks [[X]] para elementos <span class="wikilink"> clicáveis.
 * Usado como plugin do react-markdown (transformação de texto).
 *
 * @param content - Conteúdo markdown com possíveis wikilinks
 * @param onWikilinkClick - Callback chamado com o ID da nota ao clicar
 * @returns Conteúdo com wikilinks substituídos por spans clicáveis
 */
export function processWikilinks(
  content: string,
  onWikilinkClick?: (noteId: string, displayText: string) => void
): string {
  // [[Nota|Alias]] ou [[Nota]]
  return content.replace(/\[\[([^\]|#]+)(?:#[^\]|]*)?\|?([^\]]*)\]\]/g, (_, target, alias) => {
    const display = alias.trim() || target.trim()
    const noteId = slugifyNoteName(target.trim())

    if (onWikilinkClick) {
      // Em ambiente DOM: retorna HTML com data attribute
      return `<span class="wikilink" data-note-id="${noteId}" onclick="window.__wikilinkClick?.('${noteId}', '${display}')">${display}</span>`
    }

    return `[${display}](/node/${noteId})`
  })
}

/**
 * Extrai todos os wikilinks de um conteúdo markdown.
 * Retorna array de IDs das notas linkadas.
 */
export function extractWikilinks(content: string): string[] {
  const links: string[] = []
  const regex = /\[\[([^\]|#]+)(?:#[^\]|]*)?\|?[^\]]*\]\]/g
  let match

  while ((match = regex.exec(content)) !== null) {
    links.push(slugifyNoteName(match[1].trim()))
  }

  return [...new Set(links)] // deduplica
}

/**
 * Converte nome de nota Obsidian para um ID URL-safe.
 * Exemplo: "4Ps (Mix de Marketing)" → "4ps-mix-de-marketing"
 */
export function slugifyNoteName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos
    .replace(/[^\w\s-]/g, '')        // remove especiais exceto - e espaço
    .replace(/\s+/g, '-')            // espaços → hífens
    .replace(/-+/g, '-')             // múltiplos hífens → um
    .replace(/^-|-$/g, '')           // trim hífens nas bordas
}

/**
 * Extrai título de um conteúdo markdown (primeiro H1 ou frontmatter title).
 */
export function extractTitle(content: string, fallbackFilename?: string): string {
  // Tenta frontmatter title
  const frontmatterMatch = content.match(/^---\n(?:.*\n)*?title:\s*["']?([^"'\n]+)["']?\n/m)
  if (frontmatterMatch) return frontmatterMatch[1].trim()

  // Tenta primeiro H1
  const h1Match = content.match(/^#\s+(.+)$/m)
  if (h1Match) return h1Match[1].trim()

  // Fallback: nome do arquivo sem extensão
  return fallbackFilename?.replace(/\.md$/, '') ?? 'Sem título'
}

/**
 * Remove frontmatter YAML de um conteúdo markdown.
 */
export function stripFrontmatter(content: string): string {
  return content.replace(/^---\n[\s\S]*?\n---\n?/, '').trim()
}

/**
 * Parseia frontmatter YAML de forma simples (sem dependência de yaml lib).
 * Suporta: string, number, boolean, listas simples.
 */
export function parseFrontmatter(content: string): Record<string, unknown> {
  const match = content.match(/^---\n([\s\S]*?)\n---/)
  if (!match) return {}

  const result: Record<string, unknown> = {}
  const lines = match[1].split('\n')

  for (const line of lines) {
    const colonIdx = line.indexOf(':')
    if (colonIdx === -1) continue

    const key = line.slice(0, colonIdx).trim()
    const value = line.slice(colonIdx + 1).trim()

    if (!key || key.startsWith('#')) continue

    // Lista inline: [a, b, c]
    if (value.startsWith('[') && value.endsWith(']')) {
      result[key] = value
        .slice(1, -1)
        .split(',')
        .map((v) => v.trim().replace(/^["']|["']$/g, ''))
        .filter(Boolean)
      continue
    }

    // Boolean
    if (value === 'true') { result[key] = true; continue }
    if (value === 'false') { result[key] = false; continue }

    // String (remove quotes se houver)
    result[key] = value.replace(/^["']|["']$/g, '')
  }

  return result
}
